import React, { useEffect } from "react";
import {
  ArrowRight,
  BookOpen,
  CalendarDays,
  CheckCircle2,
  Clock,
  FileText,
  GraduationCap,
  Headphones,
  Laptop,
  LogIn,
  PlayCircle,
  ShieldCheck,
  Video,
} from "lucide-react";

type LearningStep = {
  number: string;
  icon: React.ReactNode;
  title: string;
  desc: string;
  keywords: string;
};

type FlowStep = {
  title: string;
  desc: string;
};

type FeatureItem = {
  title: string;
  desc: string;
};

const learningSteps: LearningStep[] = [
  {
    number: "01",
    icon: <GraduationCap size={24} />,
    title: "Choose Your Course",
    desc: "Students can select the right course according to class, subject or target exam. Mediit Academy offers structured programs for Class 7 to Class 12, CBSE, NCERT, JEE Foundation, JEE Main and NEET UG preparation.",
    keywords:
      "choose online course, Class 7 online classes, Class 8 online tuition, Class 9 coaching, Class 10 board preparation, JEE Foundation course, NEET UG course",
  },
  {
    number: "02",
    icon: <LogIn size={24} />,
    title: "Login or Register",
    desc: "Students can login with registered mobile number or email. After login, enrolled courses, live batches, study notes, assignments and online tests are available inside the student dashboard.",
    keywords:
      "student login, online course registration, student dashboard, enrolled courses, live batch access, course login help",
  },
  {
    number: "03",
    icon: <CalendarDays size={24} />,
    title: "Check Batch Schedule",
    desc: "The dashboard shows live class timing, class date, subject name, teacher details and upcoming session information according to the selected course and active batch.",
    keywords:
      "live class schedule, batch timing, online class timetable, teacher details, upcoming live classes, course schedule",
  },
  {
    number: "04",
    icon: <Video size={24} />,
    title: "Join Live Class",
    desc: "Before class time, the Join Live Class option becomes available for enrolled students. Students can attend interactive online classes and learn directly from subject mentors.",
    keywords:
      "join live class, live online class, interactive online classes, subject mentor, online tuition class, live learning platform",
  },
  {
    number: "05",
    icon: <FileText size={24} />,
    title: "Access Notes and Recordings",
    desc: "After class, students can revise with PDF notes, study material, assignments and recorded learning support whenever these resources are provided for the course.",
    keywords:
      "study notes, PDF notes, recorded class support, revision material, assignments, online study material",
  },
  {
    number: "06",
    icon: <CheckCircle2 size={24} />,
    title: "Practice Tests and Track Progress",
    desc: "Students can attempt chapter-wise tests, mock tests and real exam practice to check preparation level, identify weak topics and improve score with regular performance tracking.",
    keywords:
      "online mock tests, chapter wise tests, performance tracking, weak topic analysis, score improvement, exam practice",
  },
];

const classConnectionFlow: FlowStep[] = [
  {
    title: "Admin Creates Live Class",
    desc: "The admin can add class title, course, subject, teacher, date, time and live class link from the admin panel.",
  },
  {
    title: "Backend Saves Class Details",
    desc: "Live class details are saved in the database. Based on course and enrollment, the same class data can be shown in the student dashboard.",
  },
  {
    title: "Student Dashboard Shows Relevant Classes",
    desc: "A student should only see upcoming live classes for the course or batch in which the student is enrolled.",
  },
  {
    title: "Join Button Works According to Time",
    desc: "The Join Live Class button can be enabled near class time. Expired classes can show recorded support, missed status or class notes.",
  },
];

const studentFeatures: FeatureItem[] = [
  {
    title: "Live Interactive Classes",
    desc: "Attend subject-wise live classes with expert mentors and real-time learning support.",
  },
  {
    title: "Course-Wise Batch Schedule",
    desc: "View upcoming classes, timing, teacher and subject information in one place.",
  },
  {
    title: "PDF Notes and Study Material",
    desc: "Revise important concepts with notes, PDFs, assignments and learning resources.",
  },
  {
    title: "Recorded Class Support",
    desc: "Use recorded learning support for revision whenever it is available in the course.",
  },
  {
    title: "Doubt Solving Support",
    desc: "Clear subject doubts related to Maths, Science, Physics, Chemistry, Biology, JEE and NEET.",
  },
  {
    title: "Online Tests and Reports",
    desc: "Attempt mock tests and track performance through score reports and progress analysis.",
  },
  {
    title: "Teacher Guidance",
    desc: "Learn with structured guidance, exam strategy and subject-wise mentorship.",
  },
  {
    title: "Mobile-Friendly Access",
    desc: "Use the learning platform from desktop, laptop or mobile device.",
  },
];

const teacherFeatures: FeatureItem[] = [
  {
    title: "Assigned Batches Only",
    desc: "Teachers can manage only the batches or courses assigned to them.",
  },
  {
    title: "Class Schedule Visibility",
    desc: "Teachers can view upcoming live classes, subject details and batch timings.",
  },
  {
    title: "Live Class Join Option",
    desc: "Teachers can join scheduled live sessions from the teacher or admin dashboard.",
  },
  {
    title: "Attendance Support",
    desc: "Attendance flow can be connected with live class participation and student status.",
  },
  {
    title: "Notes and Assignment Sharing",
    desc: "Teachers can upload notes, PDFs, assignments and post-class learning material.",
  },
  {
    title: "Performance Review",
    desc: "Class-wise test performance and student progress can be reviewed for improvement planning.",
  },
];

const HowLiveClassWorks: React.FC = () => {
  useEffect(() => {
    const title =
      "How Live Online Classes Work | Live Batches, Notes, Tests - Mediit Academy";

    const description =
      "Learn how Mediit Academy live online classes work for Class 7 to 12, CBSE, NCERT, JEE Foundation, JEE Main and NEET UG students with batch schedule, live class links, notes, recordings, tests and performance tracking.";

    const keywords = [
      "Mediit Academy live classes",
      "how live class works",
      "online live classes for students",
      "live batches",
      "Class 7 online classes",
      "Class 8 online tuition",
      "Class 9 Maths Science coaching",
      "Class 10 CBSE board preparation",
      "Class 11 JEE NEET preparation",
      "Class 12 online classes",
      "CBSE online classes",
      "NCERT online learning",
      "JEE Foundation live classes",
      "JEE Main online coaching",
      "NEET UG online coaching",
      "study notes",
      "recorded classes",
      "online mock tests",
      "student dashboard",
      "teacher dashboard",
      "batch schedule",
      "doubt solving support",
    ].join(", ");

    document.title = title;

    const setMetaTag = (name: string, content: string) => {
      let tag = document.querySelector(
        `meta[name="${name}"]`
      ) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    const setPropertyTag = (property: string, content: string) => {
      let tag = document.querySelector(
        `meta[property="${property}"]`
      ) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    setMetaTag("description", description);
    setMetaTag("keywords", keywords);
    setMetaTag("robots", "index, follow");
    setMetaTag("author", "Mediit Academy");

    setPropertyTag("og:title", title);
    setPropertyTag("og:description", description);
    setPropertyTag("og:type", "website");
    setPropertyTag("og:site_name", "Mediit Academy");

    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", title);
    setMetaTag("twitter:description", description);

    let canonical = document.querySelector(
      `link[rel="canonical"]`
    ) as HTMLLinkElement | null;

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute("href", `${window.location.origin}/live-batches`);
  }, []);

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How Students Join Live Online Classes at Mediit Academy",
    description:
      "Step-by-step process for students to choose a course, login, check batch schedule, join live classes, access study notes and track performance.",
    step: learningSteps.map((step) => ({
      "@type": "HowToStep",
      name: step.title,
      text: step.desc,
    })),
  };

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "How Live Classes Work at Mediit Academy",
    description:
      "Information page explaining Mediit Academy live online class flow, live batches, student dashboard, notes, recordings, tests and admin backend connection flow.",
    url: "/live-batches",
    publisher: {
      "@type": "EducationalOrganization",
      name: "Mediit Academy",
    },
    about: [
      "Live online classes",
      "Live batches",
      "Student dashboard",
      "Teacher dashboard",
      "Course batch schedule",
      "Study notes",
      "Recorded class support",
      "Online test series",
      "Performance tracking",
      "JEE and NEET preparation",
      "CBSE and NCERT online classes",
    ],
  };

  return (
    <main className="min-h-screen bg-white font-['Inter'] text-slate-950">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(pageSchema),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(howToSchema),
        }}
      />

      {/* HERO */}
      <section
        className="relative overflow-hidden bg-gradient-to-br from-[#07111b] via-[#0b2444] to-[#123b64] px-4 py-14 text-white sm:px-6 lg:px-8"
        aria-labelledby="live-class-heading"
      >
        <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl" />
        <div className="absolute -right-24 bottom-10 h-80 w-80 rounded-full bg-blue-500/20 blur-3xl" />

        <div className="relative mx-auto grid max-w-7xl gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.2em] text-cyan-300">
              Mediit Academy Live Classes
            </p>

            <h1
              id="live-class-heading"
              className="mt-4 max-w-4xl text-4xl font-black leading-tight tracking-[-0.04em] sm:text-5xl lg:text-6xl"
            >
              How Live Online Classes Work at Mediit Academy
            </h1>

            <p className="mt-6 max-w-3xl text-base font-medium leading-8 text-slate-300 sm:text-lg">
              Mediit Academy helps students access live online classes, batch
              schedule, study notes, recorded learning support, doubt solving,
              online tests and performance tracking from one simple LMS
              dashboard.
            </p>

            <p className="sr-only">
              Live online classes, live batches, student dashboard, teacher
              dashboard, Class 7 online classes, Class 8 online tuition, Class 9
              Maths Science coaching, Class 10 CBSE board preparation, JEE
              Foundation live classes, NEET UG online coaching, study notes,
              recorded classes, online mock tests and doubt solving.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="/all-classes"
                aria-label="Explore all online courses for school JEE and NEET preparation"
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-cyan-400 px-6 py-4 text-sm font-black text-slate-950 shadow-lg shadow-cyan-500/20 transition hover:-translate-y-0.5 hover:bg-cyan-300"
              >
                Explore Courses
                <ArrowRight size={17} aria-hidden="true" />
              </a>

              <a
                href="/live-batches"
                aria-label="View live batches and online class schedule"
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-6 py-4 text-sm font-black text-white transition hover:bg-white/15"
              >
                View Live Batches
                <PlayCircle size={17} aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Live class preview card */}
          <div className="rounded-[2rem] border border-white/10 bg-white/10 p-5 shadow-2xl backdrop-blur">
            <div className="rounded-[1.5rem] bg-white p-5 text-slate-950">
              <div className="flex items-center justify-between gap-4 rounded-2xl bg-slate-50 p-4">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.15em] text-blue-600">
                    Today&apos;s Live Class
                  </p>
                  <h2 className="mt-1 text-xl font-black text-slate-950">
                    Class 7 Science Foundation
                  </h2>
                </div>

                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-600 text-white">
                  <Laptop size={23} aria-hidden="true" />
                </div>
              </div>

              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 p-4">
                  <p className="text-xs font-bold text-slate-400">Teacher</p>
                  <p className="mt-1 font-black text-slate-900">
                    Expert Mentor
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 p-4">
                  <p className="text-xs font-bold text-slate-400">Time</p>
                  <p className="mt-1 font-black text-slate-900">07:00 PM</p>
                </div>

                <div className="rounded-2xl border border-slate-200 p-4">
                  <p className="text-xs font-bold text-slate-400">Mode</p>
                  <p className="mt-1 font-black text-slate-900">Online Live</p>
                </div>

                <div className="rounded-2xl border border-slate-200 p-4">
                  <p className="text-xs font-bold text-slate-400">Material</p>
                  <p className="mt-1 font-black text-slate-900">Notes + Test</p>
                </div>
              </div>

              <a
                href="/login"
                aria-label="Login and join live online class"
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-[#07111b] px-5 py-4 text-sm font-black text-white transition hover:bg-slate-800"
              >
                Join Live Class
                <Video size={17} aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* STUDENT PROCESS */}
      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
              Student Learning Process
            </p>

            <h2 className="mt-3 text-3xl font-black text-[#071f54] sm:text-4xl">
              How Students Join Live Classes and Continue Learning
            </h2>

            <p className="mt-4 text-base font-medium leading-8 text-slate-600">
              This complete flow explains how a student selects a course, logs
              in, checks the batch schedule, joins the live class, accesses
              notes and tracks test performance.
            </p>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {learningSteps.map((step) => (
              <article
                key={step.number}
                title={step.keywords}
                className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:border-blue-200 hover:shadow-xl hover:shadow-slate-200/70"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
                    {step.icon}
                  </div>

                  <span className="text-3xl font-black text-slate-100">
                    {step.number}
                  </span>
                </div>

                <h3 className="mt-5 text-xl font-black text-slate-950">
                  {step.title}
                </h3>

                <p className="mt-3 text-sm font-medium leading-7 text-slate-600">
                  {step.desc}
                </p>

                <p className="sr-only">{step.keywords}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* BACKEND FLOW */}
      <section className="bg-slate-50 px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.18em] text-orange-500">
                Backend Connection Flow
              </p>

              <h2 className="mt-3 text-3xl font-black text-[#071f54] sm:text-4xl">
                How Live Classes Can Connect with Admin and Student Dashboard
              </h2>

              <p className="mt-4 text-base font-medium leading-8 text-slate-600">
                This page explains the UI and system flow. Existing backend
                routes do not need to be changed from this page. When the admin
                creates a live class, the same data can appear in the student
                dashboard and live batches page based on enrollment.
              </p>

              <div className="mt-7 flex flex-wrap gap-3">
                <a
                  href="/live-batches"
                  aria-label="Open live batches page"
                  className="rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white transition hover:bg-blue-700"
                >
                  Live Batches Page
                </a>

                <a
                  href="/all-classes"
                  aria-label="Open all courses page"
                  className="rounded-2xl border border-slate-300 bg-white px-5 py-3 text-sm font-black text-slate-800 transition hover:border-blue-500 hover:text-blue-700"
                >
                  All Courses Page
                </a>
              </div>
            </div>

            <div className="grid gap-4">
              {classConnectionFlow.map((item, index) => (
                <article
                  key={item.title}
                  className="flex gap-4 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"
                >
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-cyan-50 text-lg font-black text-cyan-700">
                    {index + 1}
                  </div>

                  <div>
                    <h3 className="text-lg font-black text-slate-950">
                      {item.title}
                    </h3>

                    <p className="mt-2 text-sm font-medium leading-7 text-slate-600">
                      {item.desc}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-2">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-7 shadow-sm">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
              <BookOpen size={26} aria-hidden="true" />
            </div>

            <h2 className="mt-5 text-2xl font-black text-[#071f54]">
              What Students Get in Live Learning
            </h2>

            <p className="mt-3 text-sm leading-7 text-slate-600">
              Students get a complete learning environment with live classes,
              notes, tests, doubt support and progress tracking.
            </p>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {studentFeatures.map((feature) => (
                <article
                  key={feature.title}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                >
                  <p className="text-sm font-black text-slate-800">
                    ✅ {feature.title}
                  </p>
                  <p className="mt-2 text-xs leading-5 text-slate-500">
                    {feature.desc}
                  </p>
                </article>
              ))}
            </div>
          </div>

          <div className="rounded-[2rem] border border-slate-200 bg-[#07111b] p-7 text-white shadow-sm">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 text-cyan-300">
              <Headphones size={26} aria-hidden="true" />
            </div>

            <h2 className="mt-5 text-2xl font-black">
              Teacher and Admin Side Flow
            </h2>

            <p className="mt-3 text-sm leading-7 text-slate-300">
              The teacher and admin flow can support class creation, schedule
              visibility, live session access, notes sharing and performance
              review.
            </p>

            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {teacherFeatures.map((feature) => (
                <article
                  key={feature.title}
                  className="rounded-2xl border border-white/10 bg-white/[0.06] p-4"
                >
                  <p className="text-sm font-black text-slate-100">
                    ✅ {feature.title}
                  </p>
                  <p className="mt-2 text-xs leading-5 text-slate-400">
                    {feature.desc}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-br from-blue-600 to-cyan-500 px-4 py-16 text-white sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[1fr_360px] lg:items-center">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.18em] text-cyan-100">
              Start Learning Online
            </p>

            <h2 className="mt-3 text-3xl font-black sm:text-4xl">
              Choose the Right Course and Start Live Learning with Mediit
              Academy
            </h2>

            <p className="mt-4 max-w-3xl text-base font-medium leading-8 text-blue-50">
              Mediit Academy provides a complete learning platform with live
              classes, study material, tests, doubt support, mentor guidance and
              performance tracking for better exam preparation.
            </p>
          </div>

          <div className="grid gap-3">
            <a
              href="/all-classes"
              aria-label="Explore all Mediit Academy courses"
              className="flex items-center justify-center gap-2 rounded-2xl bg-white px-6 py-4 text-sm font-black text-blue-700 transition hover:bg-slate-100"
            >
              Explore All Courses
              <ArrowRight size={17} aria-hidden="true" />
            </a>

            <a
              href="/contact"
              aria-label="Talk to academic counselor"
              className="flex items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-6 py-4 text-sm font-black text-white transition hover:bg-white/15"
            >
              Talk to Counselor
              <Clock size={17} aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      {/* BACKEND NOTE */}
      <section className="px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-slate-200 bg-slate-50 p-6 sm:p-8">
          <div className="flex gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
              <ShieldCheck size={24} aria-hidden="true" />
            </div>

            <div>
              <h3 className="text-xl font-black text-slate-950">
                Note for Backend Integration
              </h3>

              <p className="mt-2 text-sm font-medium leading-7 text-slate-600">
                This page only explains the information and UI flow. Existing
                backend functionality, routes, login system, admin panel or live
                batch logic do not need to be changed from this component. The
                best approach is to connect actual live class data from the
                admin panel live class module and show it based on course and
                student enrollment.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HowLiveClassWorks;